import crypto from "crypto-js";
import axios from "axios";
import cookie from "js-cookie"

import AccessToken from "@/helpers/oauth/AccessToken";
import { span } from '../tracer'

class Config {
    constructor(data) {
        return new Proxy(this, {
            get: function(conf, field) {
                if (field in conf) return conf[field]
                if (field in data) {
                    return typeof data[field] === 'function' ? data[field]() : data[field]
                }
                return null
            }
        })
    }
}

export default class OAuthClient {
    constructor(config) {
        this.config = new Config(config)
    }

    login() {
        this.state = this.createRandomString(40);
        const verifier = this.createRandomString(128);

        this.challenge = this.base64Url(crypto.SHA256(verifier));
        cookie.set('oauth.state', this.state)
        cookie.set('oauth.verifier', verifier)

        window.location.href = this.loginUrl()
    }

    logout() {
        return new Promise((resolve, reject) => {
            const token = this.getToken()
            axios.post(this.config.logoutUrl, null, {
                headers: {
                    Authorization: `Bearer ${token.raw}`,
                    'request-id': span._generateUUID(),
                    'span-id': span.uuid(),
                    'trace-id': span._generateUUID(),
                }
            })
                .then(res => {
                    this.saveToken(null)
                    resolve()
                })
                .catch(e => {
                    console.dir(e);
                    reject()
                });
        })

    }

    createRandomString(num) {
        return [...Array(num)].map(() => Math.random().toString(36)[2]).join('')
    }

    base64Url(string) {
        return string.toString(crypto.enc.Base64)
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '');
    }

    loginUrl() {
        return this.config.authorizeUrl
            + '?client_id=' + this.config.clientId
            + '&redirect_uri=' + this.config.redirectUrl
            + '&response_type=code'
            + '&scope=' + this.config.scope.join(',')
            + '&state=' + this.state + '&code_challenge=' + this.challenge + '&code_challenge_method=S256'
    }

    wait() {
        return new Promise((resolve, reject) => {
            const urlParams = new URLSearchParams(window.location.search);
            const code = urlParams.get('code');
            const state = urlParams.get('state');

            if (code && state) {
                if (state === cookie.get('oauth.state')) {
                    let params = {
                        grant_type: 'authorization_code',
                        client_id: this.config.clientId,
                        redirect_uri: this.config.redirectUrl,
                        code_verifier:  cookie.get('oauth.verifier'),
                        code
                    }

                    axios.post(this.config.tokenUrl, params, {
                        headers: {
                            'request-id': span._generateUUID(),
                            'span-id': span.uuid(),
                            'trace-id': span._generateUUID(),
                        }
                    })
                    .then(res => {
                        this.saveToken(res.data)
                        resolve(new AccessToken(res.data))
                    })
                    .catch(e => {
                        console.dir(e);
                        reject()
                    });
                }
            } else {
                reject()
            }
        })

    }


    renewToken() {
        return new Promise((resolve, reject) => {
            const token = this.getToken()
            let params = {
                'grant_type': 'refresh_token',
                'refresh_token': token.refreshToken,
                'client_id': this.config.clientId,
                'scope': '',
            }
            axios.post(this.config.tokenUrl, params, {
                headers: {
                    'request-id': span._generateUUID(),
                    'span-id': span.uuid(),
                    'trace-id': span._generateUUID(),
                }
            }).then((res) => {
                this.saveToken(res.data)
                resolve(new AccessToken(res.data))
            }).catch(reject)
        })

    }

    saveToken(data) {
        if (data) {
            cookie.set('oauth.token.raw', data.access_token, { expires: 30 })
            cookie.set('oauth.token.expires_in', data.expires_in, { expires: 30 })
            cookie.set('oauth.token.refresh_token', data.refresh_token, { expires: 30 })
            cookie.set('oauth.token.token_type', data.token_type, { expires: 30 })
        } else {
            cookie.remove('oauth.token.raw')
            cookie.remove('oauth.token.expires_in')
            cookie.remove('oauth.token.refresh_token')
            cookie.remove('oauth.token.token_type')
        }

    }

    getToken() {
        if (!cookie.get('oauth.token.raw')) return null
        return new AccessToken({
            access_token: cookie.get('oauth.token.raw'),
            expires_in: cookie.get('oauth.token.expires_in'),
            refresh_token: cookie.get('oauth.token.refresh_token'),
            token_type: cookie.get('oauth.token.token_type'),
        })
    }
}
